import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import ProtectedRoute from './ProtectedRoute';
import PageLayout from '@/components/layout/PageLayout';
import { Lock } from 'lucide-react';
import { TeamRole } from '@/types/roles';

interface RoleProtectedRouteProps {
  children: ReactNode;
  requiredRole?: TeamRole;
  allowedRoles?: TeamRole[];
  redirectTo?: string;
  showAccessDenied?: boolean;
}

const RoleProtectedRoute = ({ 
  children, 
  requiredRole, 
  allowedRoles, 
  redirectTo = '/dashboard',
  showAccessDenied = true 
}: RoleProtectedRouteProps) => {
  return (
    <ProtectedRoute>
      <RoleCheck 
        requiredRole={requiredRole}
        allowedRoles={allowedRoles}
        redirectTo={redirectTo}
        showAccessDenied={showAccessDenied}
      >
        {children}
      </RoleCheck>
    </ProtectedRoute>
  );
};

const RoleCheck = ({ 
  children, 
  requiredRole, 
  allowedRoles, 
  redirectTo,
  showAccessDenied 
}: RoleProtectedRouteProps) => {
  const { user } = useAuth();

  if (!user) {
    return null; // This should not happen since we're inside ProtectedRoute
  }

  const userRole = user.teamRole as TeamRole;

  const matchesRole = (roleToCheck: TeamRole) => {
    if (roleToCheck === 'admin') {
      return userRole === 'admin' || userRole === 'super_admin';
    }
    return userRole === roleToCheck;
  };
  
  // Determine if user has access
  let hasAccess = false;
  
  if (requiredRole) {
    hasAccess = matchesRole(requiredRole);
  } else if (allowedRoles) {
    hasAccess = allowedRoles.some(matchesRole);
  } else {
    hasAccess = true; // No role restrictions
  }

  if (!hasAccess) {
    if (showAccessDenied) {
      return (
        <PageLayout 
          title="Access Restricted" 
          description="You don't have permission to access this page"
        >
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Lock className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
              <p className="text-muted-foreground mb-4">
                This page requires {requiredRole || allowedRoles?.join(' or ')} access.
              </p>
              <p className="text-sm text-muted-foreground">
                Your current role: <span className="font-medium capitalize">{userRole}</span>
              </p>
            </div>
          </div>
        </PageLayout>
      );
    } else {
      return <Navigate to={redirectTo} replace />;
    }
  }

  return <>{children}</>;
};

export default RoleProtectedRoute; 